/**
 * radarSampler.ts — قراءة dBZ من صور الرادار المولَّدة.
 *
 * ── لماذا عكس التدرّج، ولماذا هو مقبول هنا ────────────────────────────────────
 *
 * بقيّة الطبقات تُرسل قيمها **خاماً** في قنوات النسيج فيقرؤها `rasterSampler` مباشرةً.
 * أمّا الرادار فألوانه **مخبوزة على الخادم** (قرارٌ اتُّخذ لأن التلوين على الـGPU كان
 * سيضاعف حجم كل إطار ويُلزمنا بنقل dBZ خاماً عبر الشبكة). فلا سبيل إلى القيمة إلا
 * بعكس التلوين.
 *
 * والعكس دقيق هنا لثلاثة أسباب مقيسة، لا لحسن الظنّ:
 *  ❶ المولّد **يقرّب القيمة إلى 0.5 dBZ** قبل التلوين (QUANT_DBZ)، فعدد الألوان
 *    الممكنة 114 لا لانهاية — والجدول العكسي يغطّيها كلّها بلا التباس.
 *  ❷ الطرفان يقرآن **نفس ملفّ التدرّج** (radarPalette.json)، فلا جدولان ينحرفان.
 *  ❸ الصورة **WebP بلا فقد**، فالبكسل الذي يصل المتصفّح هو الذي كتبه المولّد بايتاً
 *    ببايت — لا انزياح ضغط يُفسد المطابقة.
 *
 * وحيث يلتبس اللون (التدرّج يعود إلى السماوي فوق 78 dBZ) نُرجّح **الأدنى**: قيمٌ
 * كتلك لا تقع في MRMS عملياً، وترجيح الأعلى كان سيقلب رذاذاً إلى إعصار.
 */

import { radarColor } from '../config/radarPalette';
import type { RadarDomain, RadarFrame } from './mrmsRadarService';
import { RADAR_MAX_TILE_Z, radarFrameUrl, radarTilesTemplate, radarValuesTemplate } from './mrmsRadarService';

/** خطوة بناء الجدول العكسي — أدقّ من تقريب المولّد (0.5) بمرّتين. */
const LUT_STEP = 0.25;
const LUT_MIN = 0;
const LUT_MAX = 80;

/** ألفا دونها نعدّ البكسل «لا صدى» بدل أن نستخرج له قيمة من لونٍ شبه شفّاف. */
const MIN_ALPHA = 8;

interface LutEntry { r: number; g: number; b: number; dbz: number }

let lut: LutEntry[] | null = null;

function buildLut(): LutEntry[] {
    const out: LutEntry[] = [];
    for (let d = LUT_MIN; d <= LUT_MAX; d += LUT_STEP) {
        const m = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(radarColor(d));
        if (m) out.push({ r: +m[1], g: +m[2], b: +m[3], dbz: d });
    }
    return out;
}

/** أقرب dBZ للون معطى. */
function invert(r: number, g: number, b: number): number | null {
    if (!lut) lut = buildLut();
    let best = Infinity;
    let dbz: number | null = null;
    for (const e of lut) {
        const dr = e.r - r, dg = e.g - g, db = e.b - b;
        const d2 = dr * dr + dg * dg + db * db;
        // `<` لا `<=`: عند التعادل نُبقي **الأوّل** أي الأدنى قيمةً — انظر أعلى الملفّ.
        if (d2 < best) { best = d2; dbz = e.dbz; }
    }
    return dbz;
}

/** صورة مفكوكة جاهزة للقراءة، مع سياقها. */
interface Decoded { w: number; h: number; data: Uint8ClampedArray }

const cache = new Map<string, Decoded>();
const pending = new Set<string>();
/** سقف بكسلات المخزن — نحو ٤٨ ميغابايت، تكفي ~١٩٠ بلاطة أو ستّ صور نطاق. */
const CACHE_BYTES = 48 * 1024 * 1024;
let bytes = 0;

/**
 * يفكّ إطاراً إلى بكسلات (مرّة واحدة لكل ملفّ).
 *
 * لا يُنتظر: يُطلق الفكّ ويُرجع null، فيقرأ التلميح في الإطار التالي — نفس اتّفاقية
 * `rasterSampler` في هذا المشروع، وهي ما يمنع التلميح من تجميد المؤشّر بانتظار شبكة.
 */
/*
 * ── يُقرأ من المصغَّرة لا من الإطار الكامل ───────────────────────────────────
 *
 * التلميح يقرأ **بكسلاً واحداً**، وكان يدفع ثمن فكّ 4096×4096 كاملةً: رسمٌ على
 * canvas ثم `getImageData` = 67 ميغابايت من البكسلات على الخيط الرئيسي.
 *
 * والقياس على جهاز المستخدم في 2026-09-20 سمّى ذلك بالاسم: خمسة مُعالِجات
 * `IMG[radar-…webp].onload` استغرقت 706 و573 و533 و491 و471 مللي — أي أن مهمّة
 * الحجز الكبرى (5.7 ثانية) كانت هنا، لا في البلاطات. فبقي الرادار متقطّعاً بعد
 * ستّة إصلاحات داخل طبقة العرض، لأن الحاجز كان خارجها.
 *
 * والمصغَّرة (عرض 1024) تكفي تماماً: سُدس عشر البكسلات، و**اللون نفسه** لأنها
 * مأخوذة بالأقرب لا بالمتوسّط (راجع SMALL_W في mrms_to_raster.py) — فالقيمة
 * المقروءة من السلّم الفئوي هي هي، والخطأ الوحيد الممكن إزاحة بضع مئات الأمتار
 * في موضع القراءة، وهي دون دقّة إصبع المستخدم على الخريطة أصلاً.
 */
function sampleUrl(frame: RadarFrame): string {
    return radarFrameUrl(frame, true);
}

function decode(url: string): Decoded | null {
    const hit = cache.get(url);
    if (hit) return hit;
    if (pending.has(url)) return null;
    pending.add(url);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
        try {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            const ctx = c.getContext('2d', { willReadFrequently: true });
            if (!ctx) return;
            ctx.drawImage(img, 0, 0);
            const d = ctx.getImageData(0, 0, c.width, c.height);
            /*
             * الإخلاء بالبايتات لا بالعدد: المخزن صار يخلط مقاسين — بلاطة 256²
             * ربع ميغابايت، وصورة نطاق كامل ثمانية. حدُّ «ستّ مدخلات» كان يعني
             * ٤٨ ميغابايت مع الصور و١.٦ مع البلاطات، أي إمّا إسرافاً أو إخلاءً
             * لبلاطاتٍ تُطلب بعد لحظة.
             */
            cache.set(url, { w: c.width, h: c.height, data: d.data });
            bytes += d.data.length;
            while (bytes > CACHE_BYTES && cache.size > 1) {
                const oldest = cache.keys().next().value as string;
                bytes -= cache.get(oldest)!.data.length;
                cache.delete(oldest);
            }
        } catch { /* قيود أصل — تبقى الطبقة بلا تلميح */ } finally {
            pending.delete(url);
        }
    };
    img.onerror = () => pending.delete(url);
    img.src = url;
    return null;
}

function mercY(latDeg: number): number {
    return Math.log(Math.tan(Math.PI / 4 + (latDeg * Math.PI / 180) / 2));
}

/*
 * ── العيّنة من البلاطة التي تحت المؤشّر، لا من صورة النطاق كلّها ────────────
 *
 * التلميح يقرأ بكسلاً واحداً. وكان يفكّ لأجله صورة نطاقٍ كاملة: ١٠٢٤ عرضاً ≈
 * مليونا بكسل، رسماً على canvas ثم `getImageData` على الخيط الرئيسي، ومرّةً لكل
 * إطارٍ يمرّ لأن المخزن يسع ستّة والإطارات أربعة وعشرون.
 *
 * وتتبّع الأداء في 2026-09-20 سمّاه بالاسم: ٧٣ طلباً لـ`radar-opera-*-small`
 * في تسجيلٍ واحد، و`getImageData` ثاني أثقل شيء على الخيط الرئيسي بعد الخمول
 * (٣١١٧ مللي). أي أن ثمن التلميح كان يُدفع من سلاسة الحركة.
 *
 * والبلاطة تكفي وتزيد: ٢٥٦×٢٥٦ — ثلاثون ضعفاً أقلّ — وهي في كاش المتصفّح أصلاً
 * لأن الخريطة رسمتها للتوّ، والبكسل نفسه بلا فقد فالعكس اللوني لا يتأثّر.
 */
function tileDbz(prefix: string, lat: number, lng: number,
    values?: { base: number; step: number }): number | null {
    const z = RADAR_MAX_TILE_Z;
    const n = 1 << z;
    const sinLat = Math.sin(lat * Math.PI / 180);
    const fx = (lng + 180) / 360 * n;
    const fy = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * n;
    const x = Math.floor(fx), y = Math.floor(fy);
    if (x < 0 || y < 0 || x >= n || y >= n) return null;

    const url = (values ? radarValuesTemplate : radarTilesTemplate)(prefix)
        .replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
    const img = decode(url);
    if (!img) return null;

    const px = Math.min(img.w - 1, Math.floor((fx - x) * img.w));
    const py = Math.min(img.h - 1, Math.floor((fy - y) * img.h));
    const i = (py * img.w + px) * 4;
    if (values) {
        // بلاطة قيم: القناة الحمراء هي القيمة نفسها — لا عكس ألوان.
        const dbz = img.data[i] * values.step + values.base;
        return dbz >= VALUES_MIN_DBZ ? dbz : null;
    }
    if (img.data[i + 3] < MIN_ALPHA) return null;
    return invert(img.data[i], img.data[i + 1], img.data[i + 2]);
}

/** عتبة الصدى في بلاطات القيم — تطابق `MIN_DBZ` في mrms_to_raster.py. */
const VALUES_MIN_DBZ = 5;

/**
 * dBZ عند نقطة، أو null إن لم تُغطَّ أو لم تجهز الصورة بعد.
 *
 * الإسقاط **مِركاتور رأسياً** لأن الصورة مُسقَطة كذلك في المولّد (to_mercator_max)؛
 * قسمةٌ خطّية في الدرجات كانت ستُزيح القراءة عن موضعها بمقدارٍ يبلغ عشرات الكيلومترات
 * في وسط الإطار — وهو نفس الخطأ الذي قِيس بـ235 كم عند بناء الطبقة.
 */
export function sampleRadarDbz(
    domains: readonly RadarDomain[], frameOf: (d: RadarDomain) => RadarFrame | undefined,
    lat: number, lng: number,
): number | null {
    for (const d of domains) {
        const b = d.bounds;
        if (lat > b.north || lat < b.south || lng < b.west || lng > b.east) continue;
        const frame = frameOf(d);
        if (!frame) continue;
        // البلاطات حين تكون منشورة، والصورة الكاملة وحدها حين لا تكون.
        if (frame.tiles) return tileDbz(frame.tiles, lat, lng, d.values);
        const img = decode(sampleUrl(frame));
        if (!img) continue;

        const yN = mercY(b.north), yS = mercY(b.south);
        const py = Math.floor((yN - mercY(lat)) / (yN - yS) * img.h);
        const px = Math.floor((lng - b.west) / (b.east - b.west) * img.w);
        if (px < 0 || py < 0 || px >= img.w || py >= img.h) continue;

        const i = (py * img.w + px) * 4;
        if (img.data[i + 3] < MIN_ALPHA) return null;      // مغطّى بالرادار، بلا صدى
        return invert(img.data[i], img.data[i + 1], img.data[i + 2]);
    }
    return null;
}

/** يُحمّي إطارات النطاقات مسبقاً كي يجهز التلميح فور المرور. */
export function prefetchRadarSample(
    domains: readonly RadarDomain[], frameOf: (d: RadarDomain) => RadarFrame | undefined,
): void {
    for (const d of domains) {
        const f = frameOf(d);
        // لا إحماء للبلاطات: بلاطة المؤشّر تُعرف من موضعه لا من النطاق، والمتصفّح
        // حمّلها أصلاً لأجل الرسم — فالإحماء هنا تحميلٌ ثانٍ بلا فائدة.
        if (f && !f.tiles) decode(sampleUrl(f));
    }
}
